import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
    TextField, Autocomplete, Button, IconButton, InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MainTable from '../../../components/Table/PaginationTable';
import LinkedOrdersModal from './Modals/LinkedOrderModal.js';
import { fetchSuppliersByCompanyId, fetchOrderById } from '../../../redux/orderSlice';
import linkIcon from '../../../assets/images/link.svg';
import { ProductColumnDefDefaultFn } from '../columns/columnDef.js';
import { useTranslation } from 'react-i18next';
import OrderUpdatedSuccessfullyModal from '../../../components/Modals/OrderUpdatedSuccessfullyModal.js';
dayjs.extend(localizedFormat);

export const CONFIRM_ORDER_VALIDATION = yup.object().shape({
    orderNumber: yup.string().required('Purchase Order cannot be empty.'),
    supplierCompanyKeyId: yup.string().required('Supplier is required.'),
    orderDate: yup.date().required('Order date is required.'),
    orderExpectedDate: yup.date().required('Order due date is required.'),
    // orderDescription: yup.string().required('Order description is required.'),
});

const ConfirmOrder = ({ currentStep, onSubmit, handleStepChange, actionType, handleNextStep, editProductByIdFun, linkedOrderList, deleteProductByIdFun, totalRecordsLink,successModalopen,handleOrderSucessClose,handleOrderSucessOpen, productCount }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation()
    const { orderId } = useParams();
    const supplierList = useSelector(state => state.order.suppliers.data || []);
    const orderDetails = useSelector(state => state.order.orderDetails?.data || {});
    const updateOrder = useSelector(state => state.order.updateOrder || {})
    const userDetails = useSelector(state => state.auth.userDetails);
    const [modalOpen, setModalOpen] = useState(false);
    const [linkedOrders, setLinkedOrders] = useState([]);

    const [initialState, setInitialState] = useState({
        supplierCompanyKeyId: '',
        orderDate: dayjs(),
        orderNumber: '',
        orderStatus: 'Open',
        orderDescription: '',
        orderExpectedDate: dayjs(),
        linkedOrderNumber: ''
    });

    const [orderProducts, setOrderProducts] = useState([]);

    const navigateCallback = (productId, actionType, currentStep) => {
        // console.warn("In Edit Confirm Order", productId, actionType)
        // currentStep(2)
        // navigate(`/edit-product/${productId}`, { state: { actionType: 'edit',productId:productId } });
    }
    const productColumn = ProductColumnDefDefaultFn(navigateCallback, editProductByIdFun, deleteProductByIdFun);


    // const handleLinkOrder = (selectedOrders) => {
    // }

    useEffect(() => {
        if (orderId) {
            dispatch(fetchOrderById(orderId));
        }
        // Cleanup function to reset order state when component unmounts
        // return () => {
        //     resetOrderState();
        // };
    }, [dispatch, orderId]);

    useEffect(() => {
        dispatch(fetchSuppliersByCompanyId());
    }, [dispatch]);

    useEffect(() => {
        if (orderDetails) {
            setInitialState({
                supplierCompanyKeyId: orderDetails.supplierCompany?.companyKeyId || '',
                orderDate: orderDetails.orderDate ? dayjs(orderDetails.orderDate) : dayjs(),
                orderNumber: orderDetails.orderNumber || '',
                orderStatus: orderDetails.orderStatus || 'Open',
                orderDescription: orderDetails.orderDescription || '',
                orderExpectedDate: orderDetails.orderExpectedDate ? dayjs(orderDetails.orderExpectedDate) : dayjs(),
                linkedOrderNumber: orderDetails.linkedOrderNumber || ''
            });
            setOrderProducts(orderDetails.orderProducts || []);
        }
    }, [orderDetails]);

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);

    const supplierOptions = supplierList.map(supplier => ({
        label: supplier.company_name,
        value: supplier.company_key_id,
    }));

    return (
        <>
            <Formik
                initialValues={initialState}
                enableReinitialize
                validationSchema={CONFIRM_ORDER_VALIDATION}
                onSubmit={(values, { setSubmitting }) => {
                    const formattedValues = {
                        ...values,
                        orderDate: values.orderDate.format('YYYY-MM-DD'),
                        orderExpectedDate: values.orderExpectedDate.format('YYYY-MM-DD'),
                    };
                    onSubmit(formattedValues);
                    setSubmitting(false);
                }}
            >{({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, touched }) => (
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6 cs-form mb-3">
                            <TextField
                                label={t('createOrder.purchaseOrder')}
                                className='md-input'
                                name='orderNumber'
                                value={values.orderNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.orderNumber}
                                helperText={errors.orderNumber}
                            />
                        </div>
                        <div className="col-md-6 cs-form mb-3">
                            <Autocomplete
                                disablePortal
                                id="combo-box-demo"
                                className='md-input'
                                options={supplierOptions}
                                getOptionLabel={(option) => option.label || ''}
                                value={supplierOptions.find(option => option.value === values.supplierCompanyKeyId) || null}
                                onChange={(event, newValue) => {
                                    handleChange({ target: { name: 'supplierCompanyKeyId', value: newValue?.value || '' } });
                                }}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('createOrder.supplier')}
                                        error={touched.supplierCompanyKeyId && Boolean(errors.supplierCompanyKeyId)}
                                        helperText={touched.supplierCompanyKeyId && errors.supplierCompanyKeyId}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-6 cs-form mb-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label={t('createOrder.orderDate')}
                                    className='md-input'
                                    value={values.orderDate}
                                    onChange={(newValue) => {
                                        setFieldValue('orderDate', newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            className='md-input'
                                            error={errors.orderDate}
                                            helperText={errors.orderDate}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-6 cs-form mb-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label={t('createOrder.orderDue')}
                                    className='md-input'
                                    value={values.orderExpectedDate}
                                    onChange={(newValue) => {
                                        setFieldValue('orderExpectedDate', newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            className='md-input'
                                            error={errors.orderExpectedDate}
                                            helperText={errors.orderExpectedDate}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-12 cs-form mb-3">
                            <TextField
                                label={t('createOrder.description')}
                                className='md-input'
                                name='orderDescription'
                                multiline
                                rows={4}
                                value={values.orderDescription}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.orderDescription}
                                // helperText={.errors.orderDescription}
                                inputProps={{
                                    maxLength: 300,
                                }}
                                helperText={`${values.orderDescription.length}/300`}
                            />
                        </div>
                        

                        {userDetails?.company.companyRoles !== "BR" && <div className="col-md-12 cs-form mb-3">
                            <TextField
                                fullWidth
                                variant="outlined"
                                className="md-input"
                                value={values.linkedOrderNumber}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                color="primary"
                                                onClick={handleOpenModal}
                                                component="span"
                                            >
                                                {/* <SearchOutlinedIcon /> */}
                                                <span className="ms-2"><img src={linkIcon} alt="Link" /></span>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                        </div> }
                        {/* <div className="d-flex justify-content-between">
                                <button type="button" className="btn btn-light grey-btn lg-btn" onClick={() => handleStepChange(currentStep - 1)}>Back</button>
                               
                                <button
                                    className="btn btn-primary greeen-btn lg-btn"
                                    type='submit'
                                    variant="contained"
                                    onClick={handleSubmit} // Use onClick to trigger form submission
                                // disabled={ isValid || isSubmitting }
                                >
                                    {loading ? 'Submitting...' : 'Create Order'}
                                </button>
                            </div> */}
                        <div className="d-flex justify-content-between mt-3">
                            <button type="button" className="btn btn-light grey-btn lg-btn" onClick={() => handleStepChange(currentStep - 1)}>
                                {t('addProduct.previous')}
                            </button>
                            <div>
                                <button type="submit" className="btn btn-primary greeen-btn lg-btn lg-btn mx-3" onClick={() => handleStepChange(currentStep - 1)}>
                                    {actionType === 'add' ? t('addProduct.addProductbtn') : 'Add Product'}
                                </button>
                                <button type="submit" className="btn btn-primary greeen-btn lg-btn lg-btn"
                                    // onClick={() => handleStepChange(currentStep + 1)}
                                    onClick={handleSubmit} 
                                    disabled={!productCount}// Use onClick to trigger form submission
                                >
                                    {actionType === 'add' ? t('addProduct.create') : 'Save Changes'}
                                </button>
                            </div>
                        </div>

                    </div>
                </form>
            )}
            </Formik >
            {(productCount !== 0)
              // || location?.state?.actionType === ACTION.EDIT 
              ?
              <>
                <div className='mt-5 ml-2'>
                  <h1 className="fprimary">Product Details</h1>
                </div>
                <MainTable
                columns={productColumn}
                data={orderProducts}
                enablePagination={false}
                className='mt-5'
            />
              </> : null}
           
            <LinkedOrdersModal
                open={modalOpen}
                onClose={handleCloseModal}
                linkedOrderList={linkedOrderList}
                orderId={orderId}
                totalRecordsLink={totalRecordsLink}
                // orderDetails={orderDetails}
                // onLinkOrder={handleLinkOrder}
                // currentStep={currentStep}
                handleNextStep={handleNextStep}
            />
            {successModalopen && <OrderUpdatedSuccessfullyModal
                open={handleOrderSucessOpen} handleClose={handleOrderSucessClose} />}
        </>
    )
}
export default ConfirmOrder